import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSyncStore } from '../../stores/syncStore';
import { useTenantStore } from '../../stores/tenantStore';
import { apiClient } from '../../services/apiClient';
import { TranslationKey } from '../../i18n/translationKeys';
import { Banner } from '../../ui/Banner/Banner';

/**
 * Shown when the connected Acomba copy's posted history is behind the one we last synced — a restored backup
 * or an older copy of the same société.
 *
 * The signal is the high-water mark (`ControlGL.CGNoEcriture`) alone, and that is deliberate — it is the one
 * that cannot produce a false alarm. The reasoning is documented once, on the backend's
 * `WorkspaceLineageEntity.HighWaterMark`. Two routes raise the flag: the launch-time value from
 * `desktop-auth`, and a sync the backend rejects with `staleCopy`.
 *
 * The action promotes the copy (`/connect/desktop-promote`) and then rebuilds from it, because promoting makes
 * this older copy authoritative and an incremental sync would send nothing: it sits below the stored mark.
 */
export const AcombaCopyBanner: React.FC = () => {
    const { t } = useTranslation();
    const isStaleCopy = useTenantStore(s => s.isStaleCopy);
    const setStaleCopy = useTenantStore(s => s.setStaleCopy);
    const setInitialSyncRequired = useSyncStore(s => s.setInitialSyncRequired);
    const [promoting, setPromoting] = useState(false);

    if (!isStaleCopy) return null;

    const promote = async () => {
        // Read the CIU at call time rather than from the render closure, to avoid a stale-closure race in
        // React 19 concurrent mode where the stored value could change between render and click.
        const { ciu } = useTenantStore.getState();
        if (promoting || !ciu) return;
        setPromoting(true);
        try {
            await apiClient.post<void>('/connect/desktop-promote', { workspaceCiu: ciu });
            // Only the flag — setDesktopTenant would default the launched société key, high-water mark and
            // licence CIU back to null, costing the company-match check its comparison key and the next sync
            // its mark.
            setStaleCopy(false);
            setInitialSyncRequired(true);
        } catch {
            // Promote failure is non-blocking: the banner remains visible so the user can retry.
        } finally {
            setPromoting(false);
        }
    };

    return (
        <Banner
            tone="warning"
            testId="stale-copy-banner"
            className="mb-l"
            action={{
                label: t(TranslationKey.DesktopSession.StaleBanner.PromoteAction),
                onClick: () => void promote(),
                disabled: promoting,
                testId: 'stale-copy-promote-btn',
            }}
        >
            {t(TranslationKey.DesktopSession.StaleBanner.Message)}
        </Banner>
    );
};
